import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n, LANGS } from "../../lib/i18n";
import { useAuth, dashboardPath } from "../../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../ui/dropdown-menu";

const SCHOOL_TYPES = ["Basisschool", "EPB", "EPI", "MAVO", "HAVO", "VWO", "International School"];

const LangSwitch = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex rounded-full bg-osk-light p-1" data-testid="language-switch">
      {LANGS.filter((l) => l.enabled).map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
          className={`rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${lang === l.code ? "bg-white text-osk-blue shadow-sm" : "text-osk-muted"}`}>
          {l.label}
        </button>
      ))}
    </div>
  );
};

const AuthButtons = ({ onNav }) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (user) return (
    <>
      <Link to={dashboardPath(user)} onClick={onNav} className="osk-btn-outline" data-testid="header-dashboard-btn"><LayoutDashboard className="h-4 w-4" />{t("nav.dashboard")}</Link>
      <button onClick={() => { logout(); onNav?.(); nav("/"); }} className="osk-btn px-3 py-2 text-osk-muted hover:text-osk-navy" data-testid="header-logout-btn"><LogOut className="h-4 w-4" /></button>
    </>
  );
  return (
    <>
      <Link to="/inloggen" onClick={onNav} className="osk-btn-outline" data-testid="header-login-btn">{t("nav.login")}</Link>
      <Link to="/aanmelden" onClick={onNav} className="osk-btn-primary py-2.5" data-testid="header-start-btn">{t("nav.start")}</Link>
    </>
  );
};

export const Header = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const link = "text-[15px] font-bold text-osk-navy hover:text-osk-blue transition-colors";
  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-lg" data-testid="site-header">
      <div className="osk-container flex h-[72px] items-center justify-between gap-4">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          <DropdownMenu>
            <DropdownMenuTrigger className={`${link} flex items-center gap-1 outline-none`} data-testid="nav-lessons-dropdown">
              {t("nav.lessons")} <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 rounded-xl">
              <DropdownMenuItem asChild><Link to="/lessen" data-testid="nav-lessons-all">{t("nav.all")}</Link></DropdownMenuItem>
              <DropdownMenuSeparator />
              {SCHOOL_TYPES.map((s) => (
                <DropdownMenuItem key={s} asChild><Link to={`/lessen?school_type=${encodeURIComponent(s)}`} data-testid={`nav-lessons-${s}`}>{s}</Link></DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to="/hoe-het-werkt" className={link} data-testid="nav-how">{t("nav.how")}</NavLink>
          <NavLink to="/voor-docenten" className={link} data-testid="nav-teachers">{t("nav.teachers")}</NavLink>
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <LangSwitch />
          <AuthButtons />
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
          {open ? <X className="h-7 w-7 text-osk-navy" /> : <Menu className="h-7 w-7 text-osk-navy" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-white px-5 pb-6 pt-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            <Link to="/lessen" onClick={() => setOpen(false)} className={link}>{t("nav.lessons")}</Link>
            <Link to="/hoe-het-werkt" onClick={() => setOpen(false)} className={link}>{t("nav.how")}</Link>
            <Link to="/voor-docenten" onClick={() => setOpen(false)} className={link}>{t("nav.teachers")}</Link>
            <LangSwitch />
            <div className="flex flex-col gap-3 [&>*]:w-full"><AuthButtons onNav={() => setOpen(false)} /></div>
          </div>
        </div>
      )}
    </header>
  );
};
